import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/Slider";
import { Dialog, DialogTitle, DialogHeader, DialogDescription, DialogPopup } from "@/components/ui/Dialog";
import type { ScreenProps } from "./types";

export const FillContractScreen = ({ selectedScreen, setSelectedScreen }: ScreenProps) => {
  const [showContext, setShowContext] = useState(false)

  return (
    <div className="py-8 md:py-32">
      <div className="h-4 md:h-32"></div>

      {/* context dialog */}
      <Dialog open={showContext} onOpenChange={setShowContext}>
        <DialogPopup className="rounded-2xl w-[calc(100%-3rem)] md:w-[480px]">
          <DialogHeader>
            <DialogTitle className="mb-4">Add your context</DialogTitle>

            {/* progress bar */}
            <div className="w-full h-1 bg-white/10 rounded-full my-2">
              <div className="h-full bg-white rounded-full" style={{ width: '50%' }}></div>
            </div>

            <p className="text-sm text-neutral-400">Step 2 of 3</p>

            <DialogDescription className="hidden">
              Add your context to get better terms and protections tailored to your situation
            </DialogDescription>
          </DialogHeader>

          <hr className="my-1 border-white/10" />

          <div>
            <h3 className="text-white text-lg font-medium mb-4">Deal Urgency</h3>
            <div>
              <label htmlFor="urgency" className="text-sm text-neutral-400 mb-2 block">Do you still in the next 30-60 days?</label>
              <RadioGroup className="flex flex-col gap-2" id="urgency">
                <div className="flex items-center gap-2 rounded-full p-2 px-3 border border-white/10 text-white">
                  <RadioGroupItem id="urgent" value="urgent" />
                  <label htmlFor="urgent">Yes, urgently</label>
                </div>
                <div className="flex items-center gap-2 rounded-full p-2 px-3 border border-white/10 text-white">
                  <RadioGroupItem id="flexible" value="flexible" />
                  <label htmlFor="flexible">Yes, but flexible</label>
                </div>
                <div className="flex items-center gap-2 rounded-full p-2 px-3 border border-white/10 text-white">
                  <RadioGroupItem id="no-rush" value="no-rush" />
                  <label htmlFor="no-rush">I'm in no rush</label>
                </div>
                <div className="flex items-center gap-2 rounded-full p-2 px-3 border border-white/10 text-white">
                  <RadioGroupItem id="unsure" value="unsure" />
                  <label htmlFor="unsure">I'm not sure</label>
                </div>
              </RadioGroup>
            </div>

            <div className="mt-8">
              <label htmlFor="experience" className="text-sm text-neutral-400 mb-2 block">How experienced are you with contracts?</label>
              <RadioGroup className="flex flex-col gap-2" id="experience">
                <div className="flex items-center gap-2 rounded-full p-2 px-3 border border-white/10 text-white">
                  <RadioGroupItem id="beginner" value="beginner" />
                  <label htmlFor="beginner">Beginner</label>
                </div>
                <div className="flex items-center gap-2 rounded-full p-2 px-3 border border-white/10 text-white">
                  <RadioGroupItem id="somewhat-experienced" value="somewhat-experienced" />
                  <label htmlFor="somewhat-experienced">Somewhat experienced</label>
                </div>
                <div className="flex items-center gap-2 rounded-full p-2 px-3 border border-white/10 text-white">
                  <RadioGroupItem id="very-experienced" value="very-experienced" />
                  <label htmlFor="very-experienced">Very experienced</label>
                </div>
              </RadioGroup>
            </div>

            <hr className="my-8 border-white/10" />

            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setShowContext(false)}>Back</Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setShowContext(false)}>Skip for now</Button>
                <Button onClick={() => setShowContext(false)}>Next</Button>
              </div>
            </div>
          </div>
        </DialogPopup>
      </Dialog>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h3 className="text-md font-medium">Recording Contract</h3>

          <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-white/10">
            <div className="flex flex-col gap-2">
              <h3>Personalize Your Contract</h3>
              <p className="text-sm text-neutral-400 w-3/4">
                Add your context to get better terms and protections tailored to your situation
              </p>
            </div>
            <Button variant="outline" onClick={() => setShowContext(true)}>Add Context</Button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-md font-medium">Contract Details</h3>
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="firstName0" className="text-sm text-neutral-400">First Name</label>
                <Input id="firstName0" placeholder="Enter First Name" />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="lastName0" className="text-sm text-neutral-400">Last Name</label>
                <Input id="lastName0" placeholder="Enter Last Name" />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="newValue" className="text-sm text-neutral-400">New Value</label>
                <Input id="newValue" placeholder="Enter New Value" />
              </div>
              
              <div className="flex flex-col gap-2">
                <label htmlFor="profession" className="text-sm text-neutral-400">Profession</label>
                <Input id="profession" placeholder="Enter Profession" />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="gender" className="text-sm text-neutral-400">Gender</label>
            <RadioGroup className="flex gap-8" id="gender">
              <div className="flex items-center gap-2">
                <RadioGroupItem id="male" value="male">Male</RadioGroupItem>
                <label htmlFor="male">Male</label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="female" value="female">Female</RadioGroupItem>
                <label htmlFor="female">Female</label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="unspecified" value="unspecified">Unspecified (X)</RadioGroupItem>
                <label htmlFor="unspecified">Unspecified (X)</label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="undisclosed" value="undisclosed">Undisclosed (X)</RadioGroupItem>
                <label htmlFor="undisclosed">Undisclosed (X)</label>
              </div>
            </RadioGroup>
          </div>
          
          <div className="flex items-center justify-between gap-4 p-4 py-5 rounded-2xl bg-white/10 mt-4">
            <p className="flex-[15%]">Revenue Split</p>
            <Slider defaultValue={[70]} max={100} className="flex-2" />
            <p className="flex-[10%] text-right">100%</p>
          </div>

          <div className="relative flex items-center justify-between gap-4 p-4 py-5 rounded-2xl bg-white/10 mt-8">
            <div className="absolute -top-6 left-1/2 w-3/4 bg-white/30 -translate-x-1/2 rounded-xl px-4 py-2 text-xs backdrop-blur-sm">
            These inputs may seem out of range of standard contracts. Please consider adjusting your parameters
            </div>
            <p className="flex-[15%]">Advance</p>
            <Slider defaultValue={[1]} max={8} className="flex-2" />
            <p className="flex-[10%] text-right">1 year</p>
          </div>

          <footer className="flex items-center justify-end gap-4 mt-8">
            <Button className="w-48" variant="outline" onClick={() => setSelectedScreen('initial')}>Cancel</Button>
            <Button className="w-48" onClick={() => setSelectedScreen('preview')}>Continue</Button>
          </footer>
        </div>
      </div>
    </div>
  )
}

